// KPI Definition Create/Edit dialog. Owns state + validation; delegates
// rendering to inline fields (no separate Fields component — KPI has
// enough domain-specific fields that the modal is the natural home).
//
// - `kpiNumber` is NOT a form field — server-assigns on Create.
//   Edit mode shows it read-only above the form.
// - `higherIsBetter` radio is **required on Create**. Edit mode
//   tolerates `null` (indeterminate, legacy KPIs) and shows a warning
//   banner when unset.
// - `reportingFrequency` must be equal-or-coarser than
//   `measurementFrequency` — inline error on the reporting select.
// - The value-type picker at the top drives the target input
//   shape (percentage / number-with-unit / met-not-met). Clause
//   reference is always-visible above the "Service credits apply"
//   tickbox (was hidden behind the SLA toggle pre-PR-B).

import { useState } from "react";
import Modal from "../../../common/Modal";
import Button from "../../../common/Button";
import Alert from "../../../common/Alert";
import AutoIdentifierField from "../shared/AutoIdentifierField";
import EvidenceLinksField from "../shared/EvidenceLinksField";
import useSeededFormState from "../../../../hooks/useSeededFormState";
import {
  FREQUENCY_RANK,
  buildEmptyKpiFormState,
  stateFromKpi,
  toCreateKpiInput,
  type KpiFieldErrors,
  type KpiFormState,
} from "./kpiFormState";
import type {
  CreateKpiInput,
  Kpi,
  KpiValueType,
  RecurrenceFrequency,
  UpdateKpiInput,
} from "../../../../types";

export type KpiFormMode = "create" | "edit";

interface KpiFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: KpiFormMode;
  contractId: string;
  source?: Kpi;
  /** Loaded KPI list for the contract. Used in Create mode to compute the
   *  default reference number (priorMax+1) and for the fast-path duplicate
   *  check. BE collision detection remains authoritative. */
  existingKpis?: Kpi[];
  onCreate?: (input: CreateKpiInput) => Promise<unknown>;
  onUpdate?: (input: UpdateKpiInput) => Promise<unknown>;
  isSubmitting?: boolean;
}

const FREQUENCIES: { value: RecurrenceFrequency; label: string }[] = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "annually", label: "Annually" },
];

function validate(
  state: KpiFormState,
  mode: KpiFormMode,
  existingKpis?: Kpi[],
): KpiFieldErrors {
  const errors: KpiFieldErrors = {};
  if (mode === "create" && state.kpiNumberInput.trim() !== "") {
    const n = parseInt(state.kpiNumberInput, 10);
    if (!Number.isFinite(n) || n < 1) {
      errors.kpiNumberInput = "Reference number must be a positive integer.";
    } else if (existingKpis?.some((k) => k.kpiNumber === n)) {
      errors.kpiNumberInput = `Reference number ${n} is already in use.`;
    }
  }
  if (!state.name.trim()) errors.name = "Name is required";
  if (!state.definition.trim()) errors.definition = "Definition is required";
  if (!state.target.trim()) errors.target = "Target is required";
  if (!state.measurementFrequency) {
    errors.measurementFrequency = "Measurement period is required";
  }
  if (!state.reportingFrequency) {
    errors.reportingFrequency = "Reporting period is required";
  }
  if (
    state.measurementFrequency &&
    state.reportingFrequency &&
    FREQUENCY_RANK[state.reportingFrequency] <
      FREQUENCY_RANK[state.measurementFrequency]
  ) {
    errors.reportingFrequency =
      "Reporting frequency cannot be more frequent than measurement frequency";
  }
  // higherIsBetter required on Create; tolerated indeterminate on Edit.
  if (mode === "create" && state.higherIsBetter === null) {
    errors.higherIsBetter = "Direction of improvement is required";
  }
  // Amber-band %: optional, but when provided must be 1-50.
  // Blank is valid and falls through to AMBER_BAND_RELATIVE at runtime.
  if (state.amberBandPercentInput.trim() !== "") {
    const n = Number(state.amberBandPercentInput);
    if (!Number.isFinite(n) || n < 1 || n > 50) {
      errors.amberBandPercentInput =
        "Enter a value between 1 and 50, or leave blank for the 5% default.";
    }
  }
  return errors;
}

const modeTitle: Record<KpiFormMode, string> = {
  create: "Add KPI",
  edit: "Edit KPI",
};

export default function KpiFormModal({
  isOpen,
  onClose,
  mode,
  contractId,
  source,
  existingKpis,
  onCreate,
  onUpdate,
  isSubmitting,
}: KpiFormModalProps) {
  const [errors, setErrors] = useState<KpiFieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Re-seed whenever the modal opens or its target changes. The seedKey
  // encodes the mode/source pair so a React Query refetch that changes
  // existingKpis does not re-seed what the user has typed. The modal is
  // eager-mounted (only isOpen toggles) — useSeededFormState resets its
  // guard on close, so reopening with the same key (e.g. create → create)
  // re-seeds instead of showing the previous values.
  const [state, setState] = useSeededFormState<KpiFormState>(
    isOpen,
    `${mode}:${source?.id ?? "new"}`,
    () => {
      if (mode === "edit" && source) return stateFromKpi(source);
      const priorMax =
        existingKpis && existingKpis.length > 0
          ? Math.max(...existingKpis.map((k) => k.kpiNumber))
          : 0;
      return {
        ...buildEmptyKpiFormState(),
        kpiNumberInput: String(priorMax + 1),
      };
    },
    () => {
      setErrors({});
      setSubmitError(null);
    },
  );

  const patch = (p: Partial<KpiFormState>) => {
    setState((prev) => ({ ...prev, ...p }));
    const keys = Object.keys(p);
    setErrors((prev) => {
      const next = { ...prev };
      for (const k of keys) {
        delete next[k as keyof KpiFieldErrors];
      }
      // reportingFrequency error depends on measurementFrequency — clear it
      // whenever either changes.
      if (
        keys.includes("measurementFrequency") ||
        keys.includes("reportingFrequency")
      ) {
        delete next.reportingFrequency;
      }
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = validate(state, mode, existingKpis);
    if (Object.keys(v).length > 0) {
      setErrors(v);
      return;
    }
    setSubmitError(null);
    try {
      if (mode === "edit" && source && onUpdate) {
        const input: UpdateKpiInput = {
          version: source.version,
          ...toCreateKpiInput(state, contractId),
        };
        await onUpdate(input);
      } else if (mode === "create" && onCreate) {
        await onCreate(toCreateKpiInput(state, contractId));
      }
      onClose();
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : "Failed to save KPI.",
      );
    }
  };

  const showLegacyDirectionBanner =
    mode === "edit" && state.higherIsBetter === null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modeTitle[mode]}
      maxWidth="sm:max-w-2xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {mode === "edit" && source && (
          <p className="text-caption text-slate-600 dark:text-slate-400">
            KPI #{source.kpiNumber}
          </p>
        )}

        {mode === "create" && (
          <div>
            <span id="kpi-ref-number-label" className="label">
              Reference number
            </span>
            <AutoIdentifierField
              value={state.kpiNumberInput}
              label="Reference number"
              onCommit={(next) => patch({ kpiNumberInput: next })}
              validateUnique={(next) => {
                const trimmed = next.trim();
                if (trimmed === "") return Promise.resolve(null);
                const n = parseInt(trimmed, 10);
                if (!Number.isFinite(n) || n < 1) {
                  return Promise.resolve(
                    "Reference number must be a positive integer.",
                  );
                }
                if (existingKpis?.some((k) => k.kpiNumber === n)) {
                  return Promise.resolve(
                    `Reference number ${n} is already in use.`,
                  );
                }
                return Promise.resolve(null);
              }}
              helperText="Auto-generated. Click Edit to use a different number."
              externalError={errors.kpiNumberInput ?? null}
              testId="kpi-ref-number"
              disabled={isSubmitting}
            />
          </div>
        )}

        {showLegacyDirectionBanner && (
          <Alert variant="warning">
            Direction of improvement is not set. Trend colouring is inactive
            until you choose &quot;Higher is better&quot; or &quot;Lower is
            better&quot; below.
          </Alert>
        )}

        <div>
          <label htmlFor="kpi-name" className="label">
            Name <span className="text-red-600">*</span>
          </label>
          <input
            id="kpi-name"
            className="input"
            value={state.name}
            onChange={(e) => patch({ name: e.target.value })}
            disabled={isSubmitting}
            maxLength={200}
            aria-invalid={!!errors.name}
          />
          {errors.name && (
            <p className="mt-1 text-caption text-red-600 dark:text-red-400">
              {errors.name}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="kpi-definition" className="label">
            Definition <span className="text-red-600">*</span>
          </label>
          <textarea
            id="kpi-definition"
            className="input min-h-[80px]"
            value={state.definition}
            onChange={(e) => patch({ definition: e.target.value })}
            disabled={isSubmitting}
            maxLength={2000}
            aria-invalid={!!errors.definition}
          />
          {errors.definition && (
            <p className="mt-1 text-caption text-red-600 dark:text-red-400">
              {errors.definition}
            </p>
          )}
        </div>

        <fieldset>
          <legend className="label">
            Value type <span className="text-red-600">*</span>
          </legend>
          <div className="flex flex-wrap gap-3">
            {(
              [
                { value: "percentage", label: "Percentage" },
                { value: "number", label: "Number" },
                { value: "boolean", label: "Met / Not met" },
              ] as { value: KpiValueType; label: string }[]
            ).map((opt) => (
              <label
                key={opt.value}
                className="inline-flex items-center gap-2 text-caption text-slate-700 dark:text-slate-300"
              >
                <input
                  type="radio"
                  name="kpi-value-type"
                  className="accent-brand-600"
                  checked={state.valueType === opt.value}
                  onChange={() => {
                    // Switching to boolean auto-sets target to "Met" — the
                    // form's target input is hidden in that branch so this
                    // avoids submitting an empty string. Switching away
                    // from boolean clears that canned value so the user
                    // sees a fresh empty target.
                    if (opt.value === "boolean") {
                      patch({ valueType: opt.value, target: "Met" });
                    } else if (state.valueType === "boolean") {
                      patch({ valueType: opt.value, target: "" });
                    } else {
                      patch({ valueType: opt.value });
                    }
                  }}
                  disabled={isSubmitting}
                />
                {opt.label}
              </label>
            ))}
          </div>
        </fieldset>

        {state.valueType !== "boolean" && (
          // Boolean KPIs have no user-facing target: the value-type radio
          // handler writes "Met" to form state when boolean is selected
          // and that canned value is what submits. Percentage and number
          // KPIs use strict numeric inputs; percentage additionally
          // clamps to 0-100.
          <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto]">
            <div>
              <label htmlFor="kpi-target" className="label">
                Target <span className="text-red-600">*</span>
              </label>
              <input
                id="kpi-target"
                className="input"
                type="number"
                inputMode="decimal"
                step="any"
                min={state.valueType === "percentage" ? 0 : undefined}
                max={state.valueType === "percentage" ? 100 : undefined}
                value={state.target}
                onChange={(e) => patch({ target: e.target.value })}
                disabled={isSubmitting}
                placeholder={
                  state.valueType === "percentage" ? "e.g. 99.5" : "e.g. 4"
                }
                aria-invalid={!!errors.target}
              />
              {errors.target && (
                <p className="mt-1 text-caption text-red-600 dark:text-red-400">
                  {errors.target}
                </p>
              )}
            </div>
            {state.valueType === "number" && (
              <div>
                <label htmlFor="kpi-unit" className="label">
                  Unit
                </label>
                <input
                  id="kpi-unit"
                  className="input md:w-32"
                  value={state.unit}
                  onChange={(e) => patch({ unit: e.target.value })}
                  disabled={isSubmitting}
                  placeholder="e.g. hrs"
                  maxLength={16}
                />
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <label htmlFor="kpi-meas-freq" className="label">
              Measurement period <span className="text-red-600">*</span>
            </label>
            <select
              id="kpi-meas-freq"
              className="input"
              value={state.measurementFrequency}
              onChange={(e) =>
                patch({
                  measurementFrequency: e.target
                    .value as RecurrenceFrequency,
                })
              }
              disabled={isSubmitting}
              aria-invalid={!!errors.measurementFrequency}
            >
              <option value="">— Select —</option>
              {FREQUENCIES.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
            {errors.measurementFrequency && (
              <p className="mt-1 text-caption text-red-600 dark:text-red-400">
                {errors.measurementFrequency}
              </p>
            )}
          </div>
          <div>
            <label htmlFor="kpi-rep-freq" className="label">
              Reporting period <span className="text-red-600">*</span>
            </label>
            <select
              id="kpi-rep-freq"
              className="input"
              value={state.reportingFrequency}
              onChange={(e) =>
                patch({
                  reportingFrequency: e.target.value as RecurrenceFrequency,
                })
              }
              disabled={isSubmitting}
              aria-invalid={!!errors.reportingFrequency}
            >
              <option value="">— Select —</option>
              {FREQUENCIES.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
            {errors.reportingFrequency && (
              <p className="mt-1 text-caption text-red-600 dark:text-red-400">
                {errors.reportingFrequency}
              </p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="kpi-due-offset" className="label">
            Report due (days after period end)
          </label>
          <input
            id="kpi-due-offset"
            type="number"
            min={0}
            className="input"
            value={state.reportDueOffset === "" ? "" : state.reportDueOffset}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "") {
                patch({ reportDueOffset: "" });
              } else {
                const n = parseInt(v, 10);
                patch({ reportDueOffset: Number.isFinite(n) ? n : "" });
              }
            }}
            disabled={isSubmitting}
          />
        </div>

        <fieldset>
          <legend className="label">
            Direction of improvement{" "}
            {mode === "create" && <span className="text-red-600">*</span>}
          </legend>
          <div className="flex flex-wrap gap-3">
            <label className="inline-flex items-center gap-2 text-caption text-slate-700 dark:text-slate-300">
              <input
                type="radio"
                name="kpi-higher-is-better"
                className="accent-brand-600"
                checked={state.higherIsBetter === true}
                onChange={() => patch({ higherIsBetter: true })}
                disabled={isSubmitting}
              />
              Higher is better
            </label>
            <label className="inline-flex items-center gap-2 text-caption text-slate-700 dark:text-slate-300">
              <input
                type="radio"
                name="kpi-higher-is-better"
                className="accent-brand-600"
                checked={state.higherIsBetter === false}
                onChange={() => patch({ higherIsBetter: false })}
                disabled={isSubmitting}
              />
              Lower is better
            </label>
          </div>
          {errors.higherIsBetter && (
            <p className="mt-1 text-caption text-red-600 dark:text-red-400">
              {errors.higherIsBetter}
            </p>
          )}
        </fieldset>

        <div>
          <label htmlFor="kpi-clause-ref" className="label">
            Clause reference
          </label>
          <input
            id="kpi-clause-ref"
            className="input"
            value={state.kpiClauseReference}
            onChange={(e) => patch({ kpiClauseReference: e.target.value })}
            disabled={isSubmitting}
            placeholder="e.g. Schedule 5, Part 2"
            maxLength={200}
          />
        </div>

        <fieldset className="space-y-3 rounded-md border border-slate-200 p-3 dark:border-slate-700">
          <legend className="sr-only">Service credits</legend>
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              className="accent-brand-600"
              checked={state.isSla}
              onChange={(e) => patch({ isSla: e.target.checked })}
              disabled={isSubmitting}
            />
            <span className="text-caption font-medium text-slate-700 dark:text-slate-300">
              Service credits apply
            </span>
          </label>

          {state.isSla && (
            <div className="space-y-3 pl-6">
              <div>
                <label htmlFor="kpi-sla-formula" className="label">
                  Service credit formula
                </label>
                <textarea
                  id="kpi-sla-formula"
                  className="input min-h-[60px]"
                  value={state.serviceCreditFormula}
                  onChange={(e) =>
                    patch({ serviceCreditFormula: e.target.value })
                  }
                  disabled={isSubmitting}
                  maxLength={500}
                />
                <p className="mt-1 text-caption text-slate-500 dark:text-slate-400">
                  For reference only — never evaluated.
                </p>
              </div>
            </div>
          )}
        </fieldset>

        <EvidenceLinksField
          value={state.evidenceLinks}
          onChange={(v) => patch({ evidenceLinks: v })}
          disabled={isSubmitting}
        />

        <details className="group rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/60">
          <summary className="cursor-pointer select-none text-body-sm font-semibold text-slate-900 dark:text-slate-100 marker:text-slate-400">
            Advanced
          </summary>
          <div className="mt-3 space-y-3">
            <div>
              <label htmlFor="kpi-ownership" className="label">
                Ownership
              </label>
              <select
                id="kpi-ownership"
                className="input"
                value={state.ownership}
                onChange={(e) =>
                  patch({
                    ownership: (e.target.value || "") as KpiFormState["ownership"],
                  })
                }
                disabled={isSubmitting}
              >
                <option value="">Not specified</option>
                <option value="supplier">Supplier-owned</option>
                <option value="customer">Customer-owned</option>
                <option value="joint">Joint</option>
              </select>
            </div>
            <div>
              <label htmlFor="kpi-amber-band" className="label">
                Amber band %
              </label>
              <input
                id="kpi-amber-band"
                className="input"
                type="text"
                inputMode="decimal"
                value={state.amberBandPercentInput}
                onChange={(e) =>
                  patch({ amberBandPercentInput: e.target.value })
                }
                disabled={isSubmitting}
                aria-invalid={!!errors.amberBandPercentInput}
                placeholder="5"
              />
              <p className="mt-1 text-caption text-slate-500 dark:text-slate-400">
                Default 5%. Enter a value between 1 and 50 to override per-contract.
              </p>
              {errors.amberBandPercentInput && (
                <p className="mt-1 text-caption text-red-600 dark:text-red-400">
                  {errors.amberBandPercentInput}
                </p>
              )}
            </div>
          </div>
        </details>

        <div>
          <label htmlFor="kpi-notes" className="label">
            Notes
          </label>
          <textarea
            id="kpi-notes"
            className="input min-h-[60px]"
            value={state.notes}
            onChange={(e) => patch({ notes: e.target.value })}
            disabled={isSubmitting}
            maxLength={2000}
          />
        </div>

        {submitError && <Alert variant="error">{submitError}</Alert>}

        <div className="flex justify-end gap-3 pt-2">
          <Button
            variant="secondary"
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
            {mode === "edit" ? "Save changes" : "Create"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
